import React from 'react';
import { toast as hotToast, type Toast } from 'react-hot-toast';
import { FiX } from 'react-icons/fi';
import { ChatAvatar } from './ChatAvatar';

interface NewMessageToastProps {
  t: Toast;
  senderName: string;
  avatarUrl?: string | null;
  snippet: string;
  onOpen: () => void;
}

/** Message bodies can contain HTML from the rich text composer. */
export const toSnippet = (body: string | null | undefined): string => {
  if (!body) return '';
  return body
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
};

const NewMessageToast: React.FC<NewMessageToastProps> = ({
  t,
  senderName,
  avatarUrl,
  snippet,
  onOpen,
}) => (
  <div
    role="status"
    className={`pointer-events-auto flex w-[min(360px,calc(100vw-32px))] items-start gap-3 rounded-2xl border border-white/10 bg-[#1C1C1E]/95 p-3 pr-2 shadow-[0_16px_40px_rgba(0,0,0,0.55)] backdrop-blur-md transition-all duration-200 ${
      t.visible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
    }`}
  >
    <button
      type="button"
      onClick={() => {
        hotToast.dismiss(t.id);
        onOpen();
      }}
      className="flex min-w-0 flex-1 items-start gap-3 text-left cursor-pointer"
    >
      <ChatAvatar src={avatarUrl} name={senderName} size="md" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-[14px] font-semibold text-white">{senderName}</span>
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#1784FF]" aria-hidden />
        </span>
        <span className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-zinc-400">
          {snippet}
        </span>
      </span>
    </button>
    <button
      type="button"
      onClick={() => hotToast.dismiss(t.id)}
      aria-label="Dismiss"
      className="rounded-full p-1.5 text-zinc-500 transition-colors hover:bg-white/10 hover:text-white cursor-pointer"
    >
      <FiX size={14} aria-hidden />
    </button>
  </div>
);

interface ShowNewMessageToastArgs {
  conversationUuid: string;
  senderName: string;
  avatarUrl?: string | null;
  body?: string | null;
  onOpen: () => void;
}

/** One toast per conversation; a newer message replaces the previous one. */
export const showNewMessageToast = ({
  conversationUuid,
  senderName,
  avatarUrl,
  body,
  onOpen,
}: ShowNewMessageToastArgs) =>
  hotToast.custom(
    (t) => (
      <NewMessageToast
        t={t}
        senderName={senderName}
        avatarUrl={avatarUrl}
        snippet={toSnippet(body) || 'Sent an attachment'}
        onOpen={onOpen}
      />
    ),
    // Top-right keeps it clear of the message composer at the bottom.
    { id: `chat-message-${conversationUuid}`, position: 'top-right', duration: 5000 },
  );
